import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Save, Sparkles, Mic, MicOff, ChevronDown, Download, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

import ProgressSteps from "../components/ProgressSteps";
import AIGenerateModal from "../components/AIGenerateModal";
import PartidasTable from "../components/PartidasTable";
import InvoicePreview from "../components/InvoicePreview";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

import clientsData from "../data/clients.json";
import catalog from "../data/catalog.json";
import { generatePartidasFromText, reviewBudgetWithAI } from "../lib/api";
import { upsertBudget, getBudget, newBudgetId, calcTotals, fmtEUR } from "../lib/storage";
import { exportBudgetPDF } from "../lib/pdf";

const STEPS = ["Cliente", "Servicios", "Confirmar"];

const EMPTY_CLIENT = { id: "", nombre: "", nif: "", direccion: "", telefono: "", email: "" };

export default function BudgetWizard() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [step, setStep] = useState(0);
  const [budget, setBudget] = useState(() => {
    const existing = id ? getBudget(id) : null;
    if (existing) return existing;
    return {
      id: newBudgetId(),
      cliente: { ...EMPTY_CLIENT },
      tipo: "completo",
      estado: "draft",
      fecha: new Date().toISOString(),
      descripcion: "",
      partidas: [],
    };
  });

  // Skip to step 2 if budget already has data
  useEffect(() => {
    if (id && budget.partidas?.length > 0) setStep(2);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch) => setBudget((b) => ({ ...b, ...patch }));
  const updateCliente = (patch) =>
    setBudget((b) => ({ ...b, cliente: { ...b.cliente, ...patch } }));

  const handleSave = (estado = "draft") => {
    const next = { ...budget, estado, updatedAt: new Date().toISOString() };
    upsertBudget(next);
    setBudget(next);
    toast.success(estado === "final" ? "Presupuesto finalizado" : "Borrador guardado");
  };

  const handleFinishAndExport = () => {
    handleSave("final");
    exportBudgetPDF(budget);
  };

  return (
    <div className="px-10 py-10 max-w-5xl mx-auto" data-testid="budget-wizard">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
          Nuevo presupuesto · {budget.id}
        </div>
        <Button variant="ghost" size="sm" onClick={() => handleSave("draft")} className="gap-2 text-slate-500" data-testid="save-draft-btn">
          <Save className="w-4 h-4" /> Guardar borrador
        </Button>
      </div>

      <ProgressSteps steps={STEPS} current={step} />

      {step === 0 && (
        <Step1Cliente
          cliente={budget.cliente}
          tipo={budget.tipo}
          updateCliente={updateCliente}
          updateTipo={(tipo) => update({ tipo })}
        />
      )}
      {step === 1 && (
        <Step2Servicios
          budget={budget}
          update={update}
          onAdd={(parts) => update({ partidas: [...(budget.partidas || []), ...parts] })}
        />
      )}
      {step === 2 && (
        <Step3Confirmar budget={budget} update={update} onExport={handleFinishAndExport} onSave={() => handleSave("draft")} />
      )}

      <div className="flex justify-between mt-8">
        <Button
          variant="outline"
          onClick={() => (step === 0 ? navigate("/") : setStep(step - 1))}
          className="gap-2 border-slate-300"
          data-testid="wizard-prev-btn"
        >
          <ArrowLeft className="w-4 h-4" /> {step === 0 ? "Volver al inicio" : "Atrás"}
        </Button>

        {step < 2 ? (
          <Button
            onClick={() => {
              if (step === 0 && !budget.cliente?.nombre) {
                toast.error("Indica al menos el nombre del cliente");
                return;
              }
              setStep(step + 1);
            }}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            data-testid="wizard-next-btn"
          >
            Siguiente <ArrowRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            onClick={handleFinishAndExport}
            disabled={!budget.partidas?.length}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            data-testid="wizard-finish-btn"
          >
            Finalizar y descargar PDF <Download className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

/* -------------------- Step 1 -------------------- */
function Step1Cliente({ cliente, tipo, updateCliente, updateTipo }) {
  return (
    <div className="rx-card p-8" data-testid="step-1">
      <h2 className="font-display font-bold text-2xl mb-1">Datos del cliente</h2>
      <p className="text-slate-500 text-sm mb-6">
        Selecciona uno existente o introduce uno nuevo. (En esta versión los clientes son datos mock.)
      </p>

      <div className="mb-6">
        <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
          Cliente existente (mock)
        </Label>
        <Select
          value={cliente.id || ""}
          onValueChange={(v) => {
            const c = clientsData.find((x) => x.id === v);
            if (c) updateCliente(c);
          }}
        >
          <SelectTrigger className="w-full" data-testid="client-select">
            <SelectValue placeholder="Elige un cliente o rellena abajo" />
          </SelectTrigger>
          <SelectContent>
            {clientsData.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`client-option-${c.id}`}>
                {c.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <Field label="Nombre / Razón social" testid="client-name">
          <Input value={cliente.nombre} onChange={(e) => updateCliente({ nombre: e.target.value })} data-testid="client-name-input" />
        </Field>
        <Field label="NIF / CIF" testid="client-nif">
          <Input value={cliente.nif} onChange={(e) => updateCliente({ nif: e.target.value })} data-testid="client-nif-input" />
        </Field>
        <Field label="Dirección" testid="client-direccion" full>
          <Input value={cliente.direccion} onChange={(e) => updateCliente({ direccion: e.target.value })} data-testid="client-address-input" />
        </Field>
        <Field label="Teléfono" testid="client-phone">
          <Input value={cliente.telefono} onChange={(e) => updateCliente({ telefono: e.target.value })} data-testid="client-phone-input" />
        </Field>
        <Field label="Email" testid="client-email">
          <Input type="email" value={cliente.email} onChange={(e) => updateCliente({ email: e.target.value })} data-testid="client-email-input" />
        </Field>
      </div>

      <div className="mt-8 pt-6 border-t border-slate-200">
        <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3 block">
          Tipo de presupuesto
        </Label>
        <div className="grid grid-cols-2 gap-4">
          <TipoOption active={tipo === "rapido"} onClick={() => updateTipo("rapido")} title="Rápido" desc="Pocas partidas grandes. Ideal para visitas o presupuestos orientativos." testid="tipo-rapido" />
          <TipoOption active={tipo === "completo"} onClick={() => updateTipo("completo")} title="Completo" desc="Detalle por partida con cantidades reales. Listo para enviar." testid="tipo-completo" />
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full, testid }) {
  return (
    <div className={full ? "col-span-2" : ""} data-testid={`field-${testid}`}>
      <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
        {label}
      </Label>
      {children}
    </div>
  );
}

function TipoOption({ active, onClick, title, desc, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-4 rounded-md border transition-all ${
        active
          ? "border-blue-600 bg-blue-50/60 ring-2 ring-blue-600"
          : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <div className="font-display font-bold text-base mb-1">{title}</div>
      <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
    </button>
  );
}

/* -------------------- Step 2 -------------------- */
function Step2Servicios({ budget, update, onAdd }) {
  const [text, setText] = useState(budget.descripcion || "");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiPartidas, setAiPartidas] = useState(null);

  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Tu navegador no soporta dictado por voz. Usa Chrome.");
      return;
    }
    const r = new SR();
    r.lang = "es-ES";
    r.continuous = true;
    r.interimResults = true;
    let finalText = text;
    r.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalText += " " + event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setText((finalText + " " + interim).trim());
    };
    r.onerror = (e) => {
      console.error(e);
      toast.error("Error de reconocimiento de voz");
      setRecording(false);
    };
    r.onend = () => setRecording(false);
    r.start();
    recognitionRef.current = r;
    setRecording(true);
  };
  const stopVoice = () => {
    recognitionRef.current?.stop();
    setRecording(false);
  };

  const handleGenerate = async () => {
    if (!text.trim() || text.trim().length < 10) {
      toast.error("Describe el trabajo con un poco más de detalle");
      return;
    }
    update({ descripcion: text });
    setAiOpen(true);
    setAiLoading(true);
    setAiError(null);
    setAiPartidas(null);
    try {
      const res = await generatePartidasFromText({
        description: text,
        tipo: budget.tipo,
        catalog: catalog,
      });
      setAiPartidas(res.partidas || []);
    } catch (e) {
      console.error(e);
      setAiError(e.response?.data?.detail || e.message || "Error generando con IA");
    } finally {
      setAiLoading(false);
    }
  };

  const handleAddSelected = (selected) => {
    onAdd(selected);
    setAiOpen(false);
    toast.success(`${selected.length} partidas añadidas al presupuesto`);
  };

  return (
    <div className="rx-card p-8" data-testid="step-2">
      <h2 className="font-display font-bold text-2xl mb-1">Describe el trabajo</h2>
      <p className="text-slate-500 text-sm mb-6">
        Cuanto más detalle aportes (medidas, materiales, alcance), mejor será la propuesta de la IA.
      </p>

      <div className="relative">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ej: Reforma integral de baño de 6 m2. Cambiar bañera por plato de ducha de obra, alicatar paredes (2,5 m alto) con porcelánico 60x60, sustituir suelo, mueble nuevo con lavabo, inodoro suspendido, mampara cristal..."
          rows={8}
          className="resize-none text-base"
          data-testid="services-description"
        />
        <button
          type="button"
          onClick={recording ? stopVoice : startVoice}
          data-testid="voice-btn"
          className={`absolute bottom-3 right-3 p-3 rounded-full transition-all ${
            recording
              ? "bg-red-500 text-white rx-mic-pulse"
              : "bg-white border border-slate-300 text-slate-600 hover:border-blue-600 hover:text-blue-600"
          }`}
          aria-label={recording ? "Detener dictado" : "Dictar"}
        >
          {recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
      </div>
      <div className="text-xs text-slate-400 mt-2">
        {recording ? "Grabando… habla con claridad. Pulsa para parar." : "Pulsa el micrófono para dictar (Web Speech API · Chrome recomendado)."}
      </div>

      <Button
        onClick={handleGenerate}
        className="rx-spark-btn mt-6"
        data-testid="generate-ai-btn"
      >
        <Sparkles className="w-5 h-5" /> Generar partidas con IA (Claude Sonnet 4.5)
      </Button>

      {budget.partidas?.length > 0 && (
        <div className="mt-8">
          <h3 className="font-display font-bold text-base mb-3">
            Partidas añadidas ({budget.partidas.length}) · {fmtEUR(calcTotals(budget.partidas).total)}
          </h3>
          <div className="text-xs text-slate-500">
            Continuarás editando en el siguiente paso. También puedes generar más partidas con otra descripción.
          </div>
        </div>
      )}

      <AIGenerateModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        onBack={() => setAiOpen(false)}
        partidas={aiPartidas}
        loading={aiLoading}
        error={aiError}
        onAdd={handleAddSelected}
      />
    </div>
  );
}

/* -------------------- Step 3 -------------------- */
function Step3Confirmar({ budget, update, onExport, onSave }) {
  const [openDesc, setOpenDesc] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewData, setReviewData] = useState(null);

  const handleReview = async () => {
    if (!budget.partidas?.length) {
      toast.error("No hay partidas que revisar");
      return;
    }
    setReviewOpen(true);
    setReviewLoading(true);
    setReviewData(null);
    try {
      const res = await reviewBudgetWithAI({
        partidas: budget.partidas,
        description: budget.descripcion || "",
      });
      setReviewData(res);
    } catch (e) {
      console.error(e);
      toast.error("Error revisando con IA");
      setReviewOpen(false);
    } finally {
      setReviewLoading(false);
    }
  };

  const applyReview = () => {
    if (reviewData?.revised_partidas?.length) {
      update({ partidas: reviewData.revised_partidas });
      toast.success("Partidas actualizadas con sugerencias de IA");
    }
    setReviewOpen(false);
  };

  return (
    <div className="space-y-6" data-testid="step-3">
      <Button onClick={handleReview} className="rx-spark-btn" data-testid="review-ai-btn">
        <Sparkles className="w-5 h-5" /> Revisar presupuesto con IA
      </Button>

      <Collapsible open={openDesc} onOpenChange={setOpenDesc}>
        <CollapsibleTrigger asChild>
          <button className="rx-card w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-testid="desc-toggle">
            <span className="font-medium text-slate-700">Añadir descripción (opcional)</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${openDesc ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="rx-card mt-2 p-5">
            <Textarea
              value={budget.descripcion || ""}
              onChange={(e) => update({ descripcion: e.target.value })}
              rows={3}
              placeholder="Notas, observaciones o condiciones particulares…"
              data-testid="step3-desc"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <h3 className="font-display font-bold text-lg mb-3">Editar partidas</h3>
          <PartidasTable partidas={budget.partidas || []} onChange={(p) => update({ partidas: p })} />
        </div>
        <div className="lg:col-span-2">
          <h3 className="font-display font-bold text-lg mb-3">Vista previa</h3>
          <InvoicePreview budget={budget} />
          <div className="flex flex-col gap-2 mt-4">
            <Button onClick={onExport} className="bg-blue-600 hover:bg-blue-700 gap-2 w-full" data-testid="export-pdf-btn">
              <Download className="w-4 h-4" /> Descargar PDF
            </Button>
            <Button onClick={onSave} variant="outline" className="gap-2 w-full border-slate-300" data-testid="save-only-btn">
              <FileText className="w-4 h-4" /> Guardar borrador
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl" data-testid="review-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Sparkles className="w-5 h-5" /> Revisión IA
            </DialogTitle>
          </DialogHeader>
          {reviewLoading && (
            <div className="py-10 flex flex-col items-center text-slate-500 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <p>Analizando tu presupuesto…</p>
            </div>
          )}
          {!reviewLoading && reviewData && (
            <>
              <div className="space-y-2 mb-4">
                <h4 className="font-display font-bold text-sm uppercase tracking-widest text-slate-500">
                  Sugerencias
                </h4>
                {reviewData.suggestions?.length ? (
                  <ul className="space-y-2">
                    {reviewData.suggestions.map((s, i) => (
                      <li key={i} className="text-sm bg-blue-50 border border-blue-100 rounded-md p-3 text-slate-800" data-testid={`review-sug-${i}`}>
                        {s}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500">La IA no encontró sugerencias importantes.</p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <Button variant="outline" onClick={() => setReviewOpen(false)} className="border-slate-300">
                  Cerrar
                </Button>
                <Button onClick={applyReview} className="bg-blue-600 hover:bg-blue-700" data-testid="apply-review-btn">
                  Aplicar correcciones
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
